import { Injectable, Injector } from '@angular/core';
import firebase from "firebase/app";
import { CustomerdatabaseService, FILE } from './customerdatabase.service';
import { AngularFireStorage } from '@angular/fire/storage';
import { FirebaseConstants } from '../shared/FirebaseConstants';
import { AuthService } from './auth.service';
import { Packages } from '../pricing/packages';

@Injectable({
  providedIn: 'root'
})
export class FirebaseService {
  public bewerber: firebase.firestore.CollectionReference;
  public bewerbervertraege: firebase.firestore.CollectionReference;
  public bewerberVertraegeColl: firebase.firestore.CollectionReference;
  public korrespondenz: firebase.firestore.CollectionReference;
  public massenanruf: firebase.firestore.CollectionReference;
  public companies: firebase.firestore.CollectionReference;
  public workflows: firebase.firestore.CollectionReference;
  public salesmails: firebase.firestore.CollectionReference;
  public howTos: firebase.firestore.CollectionReference;
  public supporttickets: firebase.firestore.CollectionReference;
  public cvs: firebase.firestore.CollectionReference;
  public vertriebkorrespondenzen: firebase.firestore.CollectionReference;
  public indexanzeigen: firebase.firestore.CollectionReference;
  public users: firebase.firestore.CollectionReference;
  public verifiedNumbers: firebase.firestore.CollectionReference;
  public customerCompanies: firebase.firestore.CollectionReference;
  public updates: firebase.firestore.CollectionReference;
  newContractId = "";
  emailsToBeSent: any;
  private readonly checkoutPaymentMethodTypes: string[] = ['card'];

  app2;

  private _customerDB: CustomerdatabaseService | null = null;

  // Resolved lazily rather than injected. CustomerdatabaseService imports this service back, and
  // with emitDecoratorMetadata a constructor parameter puts that class into design:paramtypes,
  // which is evaluated while the other module is still initialising.
  private get customerDB(): CustomerdatabaseService {
    if (!this._customerDB) {
      this._customerDB = this.injector.get(CustomerdatabaseService);
    }
    return this._customerDB;
  }

  constructor(
    private injector: Injector,
    private storage: AngularFireStorage,
    private authService: AuthService
  ) {
    this.bewerber = firebase.firestore().collection("bewerber");
    this.bewerbervertraege = firebase.firestore().collection("kunden");
    this.bewerberVertraegeColl = firebase.firestore().collection("bewerberVertraege");
    this.korrespondenz = firebase.firestore().collection("korrespondenz");
    this.massenanruf = firebase.firestore().collection("massenanruf");
    this.companies = firebase.firestore().collection("companies");
    this.workflows = firebase.firestore().collection("workflows");
    this.salesmails = firebase.firestore().collection("salesmails");
    this.users = firebase.firestore().collection("customers");
    this.howTos = firebase.firestore().collection(FirebaseConstants.howtos);
    this.supporttickets = firebase.firestore().collection("supporttickets");
    this.cvs = firebase.firestore().collection("cvanalysen");
    this.indexanzeigen = firebase.firestore().collection("indexanzeigen");
    this.vertriebkorrespondenzen = firebase.firestore().collection("profilvertriebKorrespondenz");
    this.verifiedNumbers = firebase.firestore().collection("verifiedNumbers");
    this.customerCompanies = firebase.firestore().collection("customerCompanies");
    this.updates = firebase.firestore().collection("updates");
  }

  initSecondApp(secondApp) {
    if (this.app2 == undefined) {
      this.app2 = firebase.initializeApp(secondApp, 'app2');
    }
    return this.app2;
  }

  setSelectedRecruitingPlan(planName) {
    let plan = undefined;
    switch (planName) {
      case "recruitingAndsales25":
        plan = Packages.recruitingAndsales25;
        break;
      case "recruitingAndsales50":
        plan = Packages.recruitingAndsales50;
        break;
      case "recruitingAndsales100":
        plan = Packages.recruitingAndsales100;
        break;
      case "api-free":
        plan = Packages.apiFree;
        break;
      case "api-starter":
        plan = Packages.apiStarter;
        break;
      case "api-professional":
        plan = Packages.apiProfessional;
        break;
      case "api-pro":
        plan = Packages.apiPro;
        break;
      default:
        console.log("Invalid plan name");
        break;
    }
    if (!plan) {
      console.error('[setSelectedRecruitingPlan] Invalid plan name, skipping update');
      return;
    }
    this.updatePlanLimits(plan);
  }

  private updatePlanLimits(plan) {
    firebase.firestore().collection(FirebaseConstants.customers).doc(this.authService.returnUserId()).update({
      planLimits: plan,
    });
  }

  getMatchingResults(businessName) {
    return firebase.firestore().collection("emailsToBeSentGeneral").doc(businessName).collection("emailsToBeSent");
  }

  getSalesMailsSent(businessName) {
    return firebase.firestore().collection("profilvertriebKorrespondenz").doc(businessName).collection("kaltakquise").orderBy("created", "desc").get();
  }

  bewerberVertraege(file, id) {
    return this.bewerberVertraegeColl.doc(id).set({
      id: id,
      file: file,
      created: new Date(),
      createdGerman: (new Date()).toLocaleDateString("de-DE")
    })
  }

  getUserPermissions(uid: string): firebase.firestore.DocumentReference {
    return this.users.doc(uid);
  }

  getUserPermissionByEmail(email: string) {
    return this.users.where("email", "==", email).get();
  }

  saveUserPermissions(id, permissions) {
    return this.users.doc(id).update({
      permissions: permissions
    });
  }

  getApplicant(): firebase.firestore.CollectionReference {
    return this.bewerber;
  }

  returnUsers(): firebase.firestore.CollectionReference {
    return this.users;
  }

  addVerifiedNumbers(phoneNumber, generatedNumber) {
    return this.verifiedNumbers.add({
      phoneNumber: phoneNumber,
      accountId: this.authService.returnAccountId(),
      confirmed: false,
      generatedNumber: generatedNumber,
      userId: this.authService.returnUserId()
    });
  }

  returnVerifiedPhoneNumbers() {
    return this.verifiedNumbers.where("userId", "==", this.authService.returnUserId()).get();
  }

  returnSpecificVerifiedPhoneNumber(phoneNumber) {
    return this.verifiedNumbers
      .where("phoneNumber", "==", phoneNumber)
      .where("userId", "==", this.authService.returnUserId())
      .get();
  }

  getContract(id): firebase.firestore.DocumentReference {
    return firebase.firestore().collection(FirebaseConstants.contracts).doc(id)
  }

  getNewContractId() {
    return this.newContractId;
  }

  getCompanies() {
    return firebase.firestore().collection(FirebaseConstants.companies)
  }

  getCustomerCompanies(domain): firebase.firestore.DocumentReference {
    return this.customerCompanies.doc(domain)
  }

  getSupportTickets() {
    return firebase.firestore().collection("supporttickets")
  }

  getdistances() {
    return firebase.firestore().collection("distances")
  }

  saveDistanceCity(distance, residence, company, type) {
    console.log(distance, residence, company, type)
    return firebase.firestore().collection("distances").add({
      distance: distance,
      residence: residence,
      residencecity: residence.city,
      company: company,
      companycity: company.city,
      type: type,
      created: new Date(),
      createdGerman: (new Date()).toLocaleDateString("de-DE")
    })
  }

  saveDistance(distance, residence, company, type) {
    console.log(distance, residence, company, type)
    return firebase.firestore().collection("distances").add({
      distance: distance,
      residence: residence,
      residencezip: residence.postleitzahl,
      residencecity: residence.city,
      residencestreet: residence.strasse,
      residencehousenumber: residence.hausnummer,
      company: company,
      companyzip: company.postleitzahl,
      companycity: company.city,
      companystreet: company.strasse,
      companyhousenumber: company.hausnummer,
      type: type,
      created: new Date(),
      createdGerman: (new Date()).toLocaleDateString("de-DE")
    })
  }

  addSupportTicket(ticket) {
    return firebase.firestore().collection("supporttickets").add(ticket)
  }

  storeAICVFeedbackForMatching(id, feedback) {
    return firebase.firestore().collection("cvAIMatchingAnalysis").add({
      feedbackForMatching: feedback,
      created: new Date(),
      isFeedbackForMatching: true,
      createdISODate: new Date().toISOString(),
      createdISO: new Date().toISOString().slice(0, 10)
    })
  }

  addApplicantCVQualifications(data) {
    return firebase.firestore().collection("cvanalysen").add({
      cvQualifications: data,
      cvAnalyzed: true,
      created: new Date(),
      status: "aktiv",
      createdISO: new Date().toISOString(),
      createdISODate: new Date().toISOString().split('T')[0],
      createdGerman: new Date().toLocaleString('de-DE')
    })
  }

  storeFilesFirebase(image: FILE) {
    console.log(image);
    // const fileId = this.app2.createId();
    // console.log(fileId);
    firebase.firestore().collection(FirebaseConstants.contracts)
      .add(image)
      .then((res) => {
        this.newContractId = res.id;
        console.log(res);
      })
      .catch((err) => {
        console.log(err);
      });
  }

  getSalesEmails() {
    return this.salesmails;
  }

  getImageRef(fileStoragePath) {
    return this.storage.ref(fileStoragePath);
  }

  getUploadTask(fileStoragePath, file) {
    return this.storage.upload(fileStoragePath, file);
  }

  getSalesmails(): firebase.firestore.CollectionReference {
    return this.salesmails;
  }

  getApplicantDetail(id): firebase.firestore.DocumentReference {
    return this.bewerber.doc(id)
  }

  setJoinApiKey(joinapikey) {
    const customerNumber = this.customerDB.getCustomerNumber();
    this.companies
      .where(FirebaseConstants.kundennummer, "==", customerNumber)
      .get()
      .then((snap) => {
        console.log(snap);
        const id = snap.docs[0].id;
        this.companies.doc(id).update({
          joinapikey: joinapikey
        })
      });
  }

  createApplicant(form) {
    this.getApplicant()
      .orderBy("id", "desc")
      .get()
      .then((snap) => {
        const id = String(Number(snap.docs[0].id) + 1);
        return this.bewerber.doc(id).set({
          form: form,
          email: form.email,
          id: id
        }).then(() => {
        })
      }).catch((err) => {
        const id = "no id";
        return id
      })
  }

  updateApplicantForm(id, form) {
    console.log(form);
    return this.bewerber.doc(id).update({
      form: form
    });
  }

  updateBewerberStatus(id, pn, arbeit, form) {
    console.log(form);
    return this.bewerber.doc(id).update({
      status: "mitarbeiter",
      personalnummer: pn,
      Personalnummer: pn,
      Arbeit: arbeit,
      arbeit: arbeit,
      form: form
    });
  }

  getPicklistApplicantContracts(): firebase.firestore.CollectionReference {
    return this.bewerbervertraege
  }

  SalesAnlegen(item, email, vorlage, url, anrede) {
    return this.salesmails.add({
      customer: item,
      email: email,
      vorlage: vorlage,
      url: url,
      anrede: anrede,
      created: new Date(),
      createdGerman: (new Date()).toLocaleDateString("de-DE"),
      createdISO: (new Date()).toISOString(),
      createdISODate: (new Date()).toISOString().split("T")[0]
    })
  }

  Korrespondenzanlegen(id, art, status, date, isodate, user, message?) {
    console.log(id)
    console.log(status)
    console.log(date)
    console.log(isodate)
    return this.korrespondenz.doc(id).collection("korrespondenz").add({
      art: art,
      date: date,
      IsoDate: isodate,
      user: user,
      status: status,
      message: message || ""
    })
  }

  KorrespondenzExport(id, art, status, date, isodate, user) {
    return this.massenanruf.add({
      art: art,
      date: date,
      IsoDate: isodate,
      user: user,
      status: status
    })
  }

  getAllHowTos(): firebase.firestore.CollectionReference {
    console.log(this.howTos);
    return this.howTos;
  }

  getIndexAnzeigenStellen(): firebase.firestore.CollectionReference {
    return this.indexanzeigen;
  }

  createIndexForResumes() {
    const customerAdminEmail = this.customerDB.returnCustomerAdminEmailAdress();
    const customerNumber = this.customerDB.getCustomerNumber();
    return new Promise((resolve, reject) => {
      this.companies.where(FirebaseConstants.kundennummer, "==", customerNumber).get().then((snap) => {
        if (snap.docs.length > 0) {
          snap.docs.forEach((obj) => {
            const data = obj.data();
            if (data.indexForResumes == undefined) {
              this.companies.doc(obj.id).update({
                indexForResumes: customerAdminEmail
              })
              var item = {
                email: customerAdminEmail,
                kundennummer: customerNumber,
                new: true
              }
              resolve(item); // new
            } else {
              var item = {
                email: customerAdminEmail,
                kundennummer: customerNumber,
                new: false
              }
              resolve(item); // old
            }
          })
        }
      })
    })
  }

  authenticatephoneNumber(phoneNumber, accountId) {

  }

  getIndexForResumes() {
    const customerNumber = this.customerDB.getCustomerNumber();
    return new Promise((resolve, reject) => {
      this.companies.where(FirebaseConstants.kundennummer, "==", customerNumber).get().then((snap) => {
        if (snap.docs.length > 0) {
          snap.docs.forEach((obj) => {
            resolve(obj.data().indexForResumes);
          })
        }
      })
    });
  }

  getPlanLimits(userEmail: string): Promise<any> {
    return firebase.firestore().collection(FirebaseConstants.customers)
      .where('email', '==', userEmail)
      .get()
      .then((snap) => {
        return snap.docs[0].data().planLimits || null;
      }).catch((err) => {
        return null;
      });
  }
  getPlanLimitsForCompany(): Promise<any> {
    const customerNumber = this.customerDB.getCustomerNumber();
    // Called on app start before the customer is loaded; Firestore rejects undefined filter values.
    if (customerNumber === undefined || customerNumber === null || customerNumber === '') { return Promise.resolve(null); }
    return this.companies.where("kundennummer", "==", customerNumber).limit(1).get()
      .then((snap) => snap.docs.length ? snap.docs[0].data().planLimits : null)
      .catch(() => null);
  }


  getSelfServicePlanLimits(uid: string): Promise<any> {
    // get the doc from customers collection
    return firebase.firestore().collection("customers").doc(uid).get().then((doc) => {
      if (doc.exists) {
        return doc.data().planLimits;
      } else {
        return null;
      }
    });
  }

  getImportantMessages() {
    return firebase.firestore().collection("importantMessages");
  }

  getProfilvertriebKorrespondenz() {
    const businessName = this.customerDB.returnBusinessName();
    return this.vertriebkorrespondenzen.doc(businessName).collection("kaltakquise")
  }

  returnSentEmailsLast14Days(businessName) {
    // chose by createdISO 
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    return this.vertriebkorrespondenzen.doc(businessName).collection("kaltakquise")
      .where("created", ">=", fourteenDaysAgo)
  }

  createprofilvertriebKorrespondenz(businessName, data) {
    return this.vertriebkorrespondenzen.doc(businessName).collection("kaltakquise").add({
      feedbackObject: data.feedbackObject,
      resJob: data.resJob || {},
      logs: data.logs,
      jobTitle: data.jobTitle,
      profileURL: data.profileURL,
      email: data.email,
      profileType: data.profileType,
      businessName: businessName,
      acquisitionSubject: data.acquisitionSubject,
      htmlbody: data.htmlbody,
      applicantId: data.applicantId,
      createdTimestamp: (new Date()).getTime(),
      created: new Date(),
      createdISO: (new Date()).toISOString(),
      status: "open",
      statusNumber: 1 // sent by system
    })
  }

  // not for public use
  getCVs(): firebase.firestore.CollectionReference {
    return this.cvs;
  }

  getUserEMail(): string {
    return this.authService.returnUser();
  }

  getUserProfile() {

  }

  deleteApiKey(id) {
    return firebase.firestore().collection("customers").doc(this.authService.returnUserId())
      .collection("apiKeys").doc(id).delete()
  }

  loadAPIKeys() {
    return firebase.firestore().collection("customers").doc(this.authService.returnUserId())
      .collection("apiKeys")
      .where("static", "==", true)
      .get()
  }

  async loadLongLivedApiKeys(): Promise<any[]> {
    const uid = this.authService.returnUserId();
    console.log('[loadLongLivedApiKeys] uid:', uid);
    const keys: any[] = [];

    // Load from new top-level collection (may fail if Firestore rules not yet deployed)
    try {
      const newKeysSnap = await firebase.firestore()
        .collection("longLivedApiKeys")
        .where("uid", "==", uid)
        .get();
      console.log('[loadLongLivedApiKeys] New collection keys found:', newKeysSnap.size);
      newKeysSnap.docs.forEach(doc => {
        keys.push({ id: doc.id, ...doc.data(), isLegacy: false });
      });
    } catch (e) {
      console.warn('[loadLongLivedApiKeys] Could not read longLivedApiKeys collection:', e.message);
    }

    // Load legacy key from customer document
    try {
      const customerDoc = await firebase.firestore()
        .collection("customers").doc(uid).get();
      console.log('[loadLongLivedApiKeys] Customer doc exists:', customerDoc.exists);
      if (customerDoc.exists) {
        const data = customerDoc.data();
        console.log('[loadLongLivedApiKeys] Has longLivedApiKey field:', !!data.longLivedApiKey);
        if (data.longLivedApiKey) {
          const legacyKey = data.longLivedApiKey;
          const alreadyInNew = keys.some(k => k.hashedKey === legacyKey.hashedKey);
          if (!alreadyInNew) {
            keys.push({
              id: 'legacy',
              ...legacyKey,
              keyPreview: legacyKey.hashedKey
                ? legacyKey.hashedKey.slice(0, 8) + '...' + legacyKey.hashedKey.slice(-4)
                : 'Legacy Key',
              isLegacy: true
            });
          }
        }
      }
    } catch (e) {
      console.warn('[loadLongLivedApiKeys] Could not read customer doc:', e.message);
    }

    console.log('[loadLongLivedApiKeys] Total keys found:', keys.length);
    return keys;
  }

  async deleteLongLivedApiKey(keyId: string, isLegacy: boolean = false) {
    if (isLegacy) {
      return firebase.firestore().collection("customers").doc(this.authService.returnUserId())
        .update({ longLivedApiKey: firebase.firestore.FieldValue.delete() });
    }
    return firebase.firestore().collection("longLivedApiKeys").doc(keyId).delete();
  }

  updatePermissionOfAPIKeys(id, wholeApiKeyObject) {
    return firebase.firestore().collection("customers").doc(this.authService.returnUserId())
      .collection("apiKeys").doc(id)
      .update({
        active: wholeApiKeyObject.active || false,
        name: wholeApiKeyObject.name || "",
        permissions: wholeApiKeyObject.permissions,
        updatedAt: new Date(),
        updatedISO: new Date().toISOString()
      })

  }

  getapplicantsformatching() {
    return firebase.firestore().collection("applicantsformatching")
  }
  // subscriptions
  createCheckoutSessionSubscription(
    userId: string,
    priceId: string,
    quantity: number,
    mode: string,
    isEnterprise: boolean,
    metadata?: { [key: string]: string },
    subscriptionData?: { trial_period_days?: number },
    allowPromotionCodes: boolean = true,
    discounts?: Array<{ coupon?: string; promotion_code?: string }>
  ) {
    const sessionDoc: any = {
      price: priceId,
      success_url: window.location.origin + '/success',
      cancel_url: window.location.origin + '/payment/cancel',
      mode: mode,
      allow_promotion_codes: allowPromotionCodes,
      payment_method_types: this.checkoutPaymentMethodTypes,
      quantity: quantity,
      is_enterprise: isEnterprise,
      automatic_tax: { enabled: isEnterprise },        // ← ask Stripe to calculate
      billing_address_collection: isEnterprise ? 'required' : 'auto',
      tax_id_collection: { enabled: isEnterprise },   // ← turn on VAT-ID collection
      metadata: metadata || {},
    };
    // Optional Stripe trial period (passed through to subscription_data.trial_period_days)
    if (subscriptionData && typeof subscriptionData.trial_period_days === 'number' && subscriptionData.trial_period_days > 0) {
      sessionDoc.subscription_data = { trial_period_days: subscriptionData.trial_period_days };
      sessionDoc.trial_period_days = subscriptionData.trial_period_days;
    }
    if (discounts && discounts.length > 0) {
      sessionDoc.discounts = discounts;
      const promotionCodeId = discounts.find((d: any) => !!d?.promotion_code)?.promotion_code;
      const couponId = discounts.find((d: any) => !!d?.coupon)?.coupon;
      if (promotionCodeId) {
        sessionDoc.promotion_code = promotionCodeId;
      }
      if (couponId) {
        sessionDoc.coupon = couponId;
      }
    }
    return firebase.firestore()
      .collection('customers')
      .doc(userId)
      .collection('checkout_sessions')
      .add(sessionDoc);
  }

  /**
   * Create checkout session with multiple line items (e.g., package + AI interviews)
   */
  createCheckoutSessionWithLineItems(
    userId: string, 
    lineItems: Array<{ price: string; quantity: number }>, 
    mode: string, 
    isEnterprise: boolean,
    metadata?: { [key: string]: string },
    allowPromotionCodes: boolean = true,
    discounts?: Array<{ coupon?: string; promotion_code?: string }>,
    subscriptionData?: { trial_period_days?: number }
  ) {
    const sessionDoc: any = {
      line_items: lineItems,
      success_url: window.location.origin + '/success',
      cancel_url: window.location.origin + '/payment/cancel',
      mode: mode,
      payment_method_types: this.checkoutPaymentMethodTypes,
      is_enterprise: isEnterprise,
      allow_promotion_codes: allowPromotionCodes,
      automatic_tax: { enabled: isEnterprise },
      billing_address_collection: isEnterprise ? 'required' : 'auto',
      tax_id_collection: { enabled: isEnterprise },
      metadata: metadata || {},
    };
    if (discounts && discounts.length > 0) {
      sessionDoc.discounts = discounts;
      const promotionCodeId = discounts.find((d: any) => !!d?.promotion_code)?.promotion_code;
      const couponId = discounts.find((d: any) => !!d?.coupon)?.coupon;
      if (promotionCodeId) {
        sessionDoc.promotion_code = promotionCodeId;
      }
      if (couponId) {
        sessionDoc.coupon = couponId;
      }
    }
    if (subscriptionData && typeof subscriptionData.trial_period_days === 'number' && subscriptionData.trial_period_days > 0) {
      sessionDoc.subscription_data = { trial_period_days: subscriptionData.trial_period_days };
      sessionDoc.trial_period_days = subscriptionData.trial_period_days;
    }
    return firebase.firestore()
      .collection('customers')
      .doc(userId)
      .collection('checkout_sessions')
      .add(sessionDoc);
  }

  createBillingPortalSession(userId: string, otherreturnLinkOne?: string, otherreturnLinkTwo?: string) {
    let returnLinkOne = "/pricing";
    let returnLinkTwo = "/pricing";
    if (otherreturnLinkOne) {
      returnLinkOne = otherreturnLinkOne;
    }
    if (otherreturnLinkTwo) {
      returnLinkTwo = otherreturnLinkTwo;
    }
    return firebase.firestore()
      .collection('customers')
      .doc(userId)
      .collection('portal_sessions')
      .add({
        return_url: window.location.origin + returnLinkOne,
        success_url: window.location.origin + returnLinkTwo,
      });
  }

  // one time payment 
  // createCheckoutSessionOneTimePayment(userId: string,priceId: string,quantity: number) {
  //   return firebase.firestore()
  //     .collection('customers')
  //     .doc(userId)
  //     .collection('checkout_sessions')
  //     .add({
  //       price: priceId,  
  //       success_url: window.location.origin + '/success',
  //       cancel_url: window.location.origin + '/payment/cancel',
  //       mode: 'payment',
  //       quantity: quantity,
  //     });
  // }
  getUnsentEmailsCount(): firebase.firestore.DocumentReference {
    return firebase.firestore().collection('metadata').doc('emailCounters')
  }

  saveEmailsToBeSentLocally(emails) {
    this.emailsToBeSent = emails;
  }

  returnEmailsToBeSentLocally() {
    return this.emailsToBeSent;
  }

  async deleteBlacklist(allEntries: []): Promise<void> {
    const db = firebase.firestore();

    const snapshot = await db
      .collection("profilvertriebKorrespondenz")
      .doc("JOB AG")
      .collection("kaltakquise")
      .where("feedbackObject.blacklist", "!=", null)
      .orderBy("feedbackObject.blacklist")
      .orderBy("created", "desc")
      .limit(100)
      .get();

    if (snapshot.empty) {
      console.log("Größer der collection", allEntries.length);
      console.log("✅ Alle blacklists wurden gelöscht.");
      return;
    }

    const deletePromises: Promise<void>[] = snapshot.docs.map((doc) => {
      return doc.ref.update({
        "feedbackObject.blacklist": firebase.firestore.FieldValue.delete(),
      })
        .then(() => {
          console.log(`✔️ blacklist gelöscht für ${doc.id}`);
        })
        .catch((err) => {
          console.error(`❌ Fehler beim Löschen von ${doc.id}:`, err);
        });
    });

    await Promise.all(deletePromises);

    console.log("⏳ Nächster Durchlauf in 45 Sekunden...");

    setTimeout(() => {
      this.deleteBlacklist(allEntries);
    }, 45000);
  }

  returnSalesEmailsNormalCustomers(businessName): firebase.firestore.CollectionReference {
    return firebase.firestore().collection("emailsToBeSentGeneral").doc(businessName).collection("emailsToBeSent")
  }

  updateEmailsToBeSentTrueSparkasse(id) {
    return firebase.firestore().collection("emailsToBeSentSparkasse").doc(id).update({
      sent: false,
      sentDate: new Date(),
      user: this.authService.returnUser(),
      sentISODate: new Date().toISOString()
    })
  }

  updateAttachmentInEmailsToBeSentForEveryApplicantWithSameId(id, attachmentURL) {
    return firebase.firestore().collection("emailsToBeSentGeneral").doc(this.customerDB.returnBusinessName()).collection("emailsToBeSent").where("applicantId", "==", id).get()
      .then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          doc.ref.update({
            attachmentURL: attachmentURL
          });
        });
      })
      .catch((error) => {
        console.error("Error updating documents: ", error);
      });
  }

  updateAttachmentInEmailsToBeSent(id, attachmentURL) {
    // let customerNumber = this.customerDB.getCustomerNumber();
    const collectionReference = firebase.firestore().collection("emailsToBeSentGeneral").doc(this.customerDB.returnBusinessName()).collection("emailsToBeSent");
    return collectionReference.doc(id).update({
      attachmentURL: attachmentURL
    })
  }

  getEmailToBeSentById(id) {
    const collectionName = "emailsToBeSentGeneral";
    return firebase.firestore().collection(collectionName)
      .doc(this.customerDB.returnBusinessName())
      .collection("emailsToBeSent")
      .doc(id).get()
      .then((doc) => {
        if (doc.exists) {
          return doc.data();
        } else {
          console.log("No such document!");
          return null;
        }
      })
      .catch((error) => {
        console.log("Error getting document:", error);
        return null;
      });
  }

  updateDealValue(id, dealValue) {
    return firebase.firestore().collection("profilvertriebKorrespondenz").doc(this.customerDB.returnBusinessName())
      .collection("kaltakquise").doc(id).update({
        dealValue: dealValue,
        type: "dealValueChange",
        lastUpdated: new Date(),
        lastUpdatedISO: new Date().toISOString()
      })
      .then(() => {
        this.updateActivityOnLead(id, dealValue, "dealValue", "dealValueChange")
      })
  }

  updateSalesmailNotes(id, notes) {
    return firebase.firestore().collection("profilvertriebKorrespondenz").doc(this.customerDB.returnBusinessName())
      .collection("kaltakquise").doc(id).update({
        notes: notes,
        type: "notesChange",
        lastUpdated: new Date(),
        lastUpdatedISO: new Date().toISOString()
      })
      .then(() => {
        this.updateActivityOnLead(id, notes, "notes", "notesChange")
      })
  }

  updateActivityOnLead(id, value, type, typeOfChange) {
    // array union of all activities
    return firebase.firestore().collection("profilvertriebKorrespondenz").doc(this.customerDB.returnBusinessName())
      .collection("kaltakquise").doc(id).update({
        activities: firebase.firestore.FieldValue.arrayUnion({
          type: type,
          value: value,
          typeOfChange: typeOfChange,
          created: new Date(),
          createdISO: new Date().toISOString()
        })
      })
  }

  updateSalesmailStatus(id, status) {
    return firebase.firestore().collection("profilvertriebKorrespondenz").doc(this.customerDB.returnBusinessName())
      .collection("kaltakquise").doc(id).update({
        status: status,
        type: "statusChange",
        lastUpdated: new Date(),
        lastUpdatedISO: new Date().toISOString(),
        statusNumber: 2 // work in progress
      })
      .then(() => {
        this.updateActivityOnLead(id, status, "status", "statusChange")
      })
  }

  getEmailToBeSentByIdForSalesCorrespondence(id) {
    const businessName = this.customerDB.returnBusinessName();
    console.log("businessName", businessName);
    return firebase.firestore().collection("profilvertriebKorrespondenz").doc(businessName)
      .collection("kaltakquise").doc(id).get()
      .then((doc) => {
        if (doc.exists) {
          return doc.data();
        } else {
          console.log("No such document!");
          return null;
        }
      })
      .catch((error) => {
        console.log("Error getting document:", error);
        return null;
      });
  }

  updateEmailsToBeSentTrueProserv(id) {
    return firebase.firestore().collection("emailsToBeSent").doc(id).update({
      sent: false,
      sentDate: new Date(),
      user: this.authService.returnUser(),
      sentISODate: new Date().toISOString()
    })
  }

  updateEmailsToBeSentTrue(businessName, id) {
    return firebase.firestore().collection("emailsToBeSentGeneral").doc(businessName)
      .collection("emailsToBeSent").doc(id).update({
        sent: false,
        sentDate: new Date(),
        user: this.authService.returnUser(),
        sentISODate: new Date().toISOString()
      })
  }

  returnSalesEmailsNormalCustomersSinceLastLogin(businessName, lastLogin) {
    return firebase.firestore().collection("emailsToBeSentGeneral").doc(businessName)
      .collection("emailsToBeSent").where("created", ">", lastLogin)
  }

  pullAdditionalInfoOnEmailToBeSent(businessName): firebase.firestore.CollectionReference {
    return firebase.firestore().collection("emailsToBeSentGeneralWithSeparatedData")
      .doc(businessName).collection("emailsToBeSent")
  }

  pullAdditionalInfoOnEmailToBeSentDetailsByIdWithDocumentReference(businessName, id): firebase.firestore.DocumentReference {
    return firebase.firestore().collection("emailsToBeSentGeneralWithSeparatedData")
      .doc(businessName).collection("emailsToBeSent").doc(id)
  }

  returnSalesEmails(): firebase.firestore.CollectionReference {
    return firebase.firestore().collection("emailsToBeSent")
  }

  // returnSalesEmails(): firebase.firestore.CollectionReference {
  //   return firebase.firestore().collection("emailsToBeSent")
  // }

  returnSalesEmailsSparkasse(): firebase.firestore.CollectionReference {
    return firebase.firestore().collection("emailsToBeSentSparkasse")
  }

  deleteSalesEmailsSparkasse(id) {
    return firebase.firestore().collection("emailsToBeSentSparkasse").doc(id).delete()
  }

  deleteSalesEmailsGeneral(id, businessName) {
    return firebase.firestore().collection("emailsToBeSentGeneral").doc(businessName)
      .collection("emailsToBeSent").doc(id).delete()
  }

  // Deletes every emailsToBeSent doc for a given applicantId. Returns the number of docs removed.
  // Firestore batches are limited to 500 ops, so we chunk to be safe.
  async deleteSalesEmailsByApplicantId(applicantId: string, businessName: string): Promise<number> {
    const ref = firebase.firestore()
      .collection("emailsToBeSentGeneral")
      .doc(businessName)
      .collection("emailsToBeSent")
      .where("applicantId", "==", applicantId);
    const snap = await ref.get();
    if (snap.empty) return 0;

    const docs = snap.docs;
    const chunkSize = 450;
    for (let i = 0; i < docs.length; i += chunkSize) {
      const batch = firebase.firestore().batch();
      docs.slice(i, i + chunkSize).forEach((d) => batch.delete(d.ref));
      await batch.commit();
    }
    return docs.length;
  }

  deleteSalesEmails(id) {
    return firebase.firestore().collection("emailsToBeSent").doc(id).delete()
  }

  updateEmailContentToBeSent(id, htmlbody) {
    const customerNumber = this.customerDB.getCustomerNumber();
    const businessName = this.customerDB.returnBusinessName();
    console.log("businessName", businessName);
    console.log("customerNumber", customerNumber);
    const collectionReference = firebase.firestore().collection("emailsToBeSentGeneral").doc(businessName).collection("emailsToBeSent");
    return collectionReference.doc(id).update({
      htmlbody: htmlbody
    })
  }


  getProducts() {
    return firebase.firestore().collection('products');
  }

  getPrices(productId: string) {
    return firebase.firestore().collection('products').doc(productId).collection("prices");
  }

  sendSalesEmails(id) { // Cloud function onUpdateEmailsToBeSentGeneral
    return firebase.firestore().collection("emailsToBeSent").doc(id)
      .update({
        sent: true,
        sentDate: new Date(),
        user: this.authService.returnUser(),
        sentISODate: new Date().toISOString()
      })
  }

  sendSalesEmailsSparkasse(id) { // Cloud function onUpdateEmailsToBeSentGeneral
    return firebase.firestore().collection("emailsToBeSentSparkasse").doc(id)
      .update({
        sent: true,
        sentDate: new Date(),
        user: this.authService.returnUser(),
        sentISODate: new Date().toISOString()
      })
  }

  generateMONAIMAPAccount(brandingInfo) {
    return firebase.firestore().collection("MONAIMAPAccounts").add({
      brandingInfo: brandingInfo,
      created: new Date(),
      createdISO: new Date().toISOString(),
      createdISODate: new Date().toISOString().split('T')[0]
    })
  }

  sendSalesEmailsGeneral(id) { // Cloud function onUpdateEmailsToBeSentGeneral
    const businessName = this.customerDB.returnBusinessName();
    return firebase.firestore().collection("emailsToBeSentGeneral").doc(businessName)
      .collection("emailsToBeSent").doc(id)
      .update({
        sent: true,
        sentDate: new Date(),
        // profileType: "applicantProfileForCustomer",
        user: this.authService.returnUser(),
        sentISODate: new Date().toISOString()
      })
  }

  async sendSalesEmailsGeneralWithAttachmentURL(id) {
    const businessName = this.customerDB.returnBusinessName();
    return firebase.firestore().collection("emailsToBeSentGeneral").doc(businessName)
      .collection("emailsToBeSent").doc(id).get()
      .then((doc) => {
        if (doc.exists) {
          const data = doc.data();
          if (data && data.attachmentURL) {
            this.sendSalesEmailsGeneralWithAttachmentURLConfirmed(id, businessName)
              .then(() => {
              })
              .catch((error) => {
              });
          } else {
            console.log("Attachment URL is not present or empty");
            this.sendSalesEmailsGeneral(id)
              .then(() => {
              })
              .catch((error) => {
              });
          }
        } else {
          console.log("No such document!");
        }
      })
  }

  sendSalesEmailsGeneralWithAttachmentURLConfirmed(id, businessName) {
    return firebase.firestore().collection("emailsToBeSentGeneral").doc(businessName)
      .collection("emailsToBeSent").doc(id)
      .update({
        sent: true,
        sentDate: new Date(),
        profileType: "profilvertriebWithoutCreatingProfile",
        user: this.authService.returnUser(),
        sentISODate: new Date().toISOString()
      })
  }

  updateLastLogin(uid) {
    return firebase.firestore().collection("customers").doc(uid).update({
      lastLogin: new Date(),
      lastLoginISO: new Date().toISOString(),
      lastLoginGermanDate: new Date().toLocaleDateString("de-DE")
    })
  }

  returnLastLogin() {
    const uid = this.authService.returnUserId();
    return firebase.firestore().collection("customers").doc(uid).get();
  }

  markMatchesAsRead() {
    const userId = this.authService.returnUserId();
    return firebase.firestore().collection("customers").doc(userId).update({
      matchesReadDate: new Date()
    })
  }

  /**
   * Function to save the video generation information for a particular customer in the database (mona-pwa-firebase)
   * 
   * @author Chandrika
   * 
   * @param jobOfferId 
   * @param customerName 
   * @param lengthOfVideosGenerated 
   * @param avatarId 
   * @param languagesArray 
   * @returns 
   */

  saveVerificationToken(email, token) {
    return firebase.firestore().collection(FirebaseConstants.customers)
      .where('email', '==', email)
      .get()
      .then((snap) => {
        console.log("snap", snap);
        snap.forEach((doc) => {
          firebase.firestore().collection(FirebaseConstants.customers).doc(doc.id).update({
            emailVerificationToken: token
          });
        });
      });
  }

  // agents API
  getProfile(): any {
    return firebase.firestore().collection(FirebaseConstants.branding);
  }

  getAgents(): any {
    return firebase.firestore().collection(FirebaseConstants.agents);
  }

  getChats(): any {
    return firebase.firestore().collection('chats');
  }

  getAgentLogs(): any {
    return firebase.firestore().collection('agentLogs');
  }

  getApiRequestLogs(): any {
    return firebase.firestore().collection('apiRequestLogs');
  }

  getIntegrations(): any {
    return firebase.firestore().collection('integrations');
  }

  verifyEmail(token: string): Promise<{ uid: string | null; email: string | null }> {
    console.log('[FirebaseService] verifyEmail called with token:', token);
    return firebase.firestore().collection(FirebaseConstants.customers)
      .where('emailVerificationToken', '==', token)
      .get()
      .then((snap) => {
        console.log('[FirebaseService] Found', snap.size, 'documents matching token');
        let uid: string | null = null;
        let email: string | null = null;
        snap.forEach((doc) => {
          if (!uid) {
            uid = doc.id;
            const data = doc.data();
            email = data?.email || null;
            console.log('[FirebaseService] Customer doc found:', { uid, email, hasEmail: !!data?.email });
          }
          firebase.firestore().collection(FirebaseConstants.customers).doc(doc.id).update({
            emailVerified: true,
            emailVerificationToken: null
          });
          this.authService.setUserEmailToVerified(doc.id);
        });
        console.log('[FirebaseService] Returning:', { uid, email });
        return { uid, email };
      });
  }

  private static readonly ALLOWED_CLIENT_PLANS = [
    'agents-free', 'free', 'recruiting-free', 'hybrid-free', 'api-free', 'api-payg'
  ];

  async updateUserPlan(plan): Promise<void> {
    if (!FirebaseService.ALLOWED_CLIENT_PLANS.includes(plan)) {
      console.error(`[updateUserPlan] Rejected: '${plan}' is not a free plan. Paid plans must be set server-side via Stripe webhook.`);
      throw new Error('Client-side plan updates are only allowed for free plans');
    }

    const userEmail = this.authService.returnUser();
    const uid = this.authService.returnUserId();
    if (!userEmail && !uid) {
      throw new Error('Cannot update plan without an authenticated user');
    }

    const collection = firebase.firestore().collection(FirebaseConstants.customers);
    const docIds = new Set<string>();

    if (uid) {
      const ownDoc = await collection.doc(uid).get();
      if (ownDoc.exists) {
        docIds.add(uid);
      }
    }
    if (userEmail) {
      const snap = await collection.where('email', '==', userEmail).get();
      snap.forEach((doc) => docIds.add(doc.id));
    }

    if (docIds.size === 0) {
      throw new Error('No customer document found for the current user');
    }

    // Every duplicate document for this identity has to be kept in sync. A later
    // read may resolve a different one and would otherwise see a stale plan.
    await Promise.all(Array.from(docIds).map(id => collection.doc(id).set({ plan: plan }, { merge: true })));
    console.log(`[updateUserPlan] Plan '${plan}' written to ${docIds.size} customer document(s)`);
  }

  // Resolves with the provisioned kundennummer, or null when it does not arrive
  // within timeoutMs. Callers must never block their UI indefinitely on this:
  // free plans have no Stripe webhook that assigns a kundennummer, so without a
  // deadline the listener can stay open for the whole session.
  waitForCustomerNumberToBeSetWithDbListeningToChangesByEmail(timeoutMs: number = 20000): Promise<number | null> {
    const email = this.authService.returnUser();
    const uid = this.authService.returnUserId();

    const readCustomerNumber = (data: any): number | null => {
      const value = data ? data.kundennummer : null;
      const parsed = typeof value === 'number' ? value : parseInt(String(value === null || value === undefined ? '' : value), 10);
      return !isNaN(parsed) && parsed !== 0 ? parsed : null;
    };

    return new Promise<number | null>((resolve) => {
      if (!uid && !email) {
        console.warn('[waitForCustomerNumber] Missing uid/email, skipping query');
        resolve(null);
        return;
      }

      let settled = false;
      let unsubscribe: (() => void) | null = null;
      let timer: any = null;

      const settle = (value: number | null) => {
        if (settled) {
          return;
        }
        settled = true;
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        if (unsubscribe) {
          try { unsubscribe(); } catch { }
          unsubscribe = null;
        }
        resolve(value);
      };

      timer = setTimeout(() => {
        console.warn('[waitForCustomerNumber] Timed out after', timeoutMs, 'ms without a kundennummer');
        settle(null);
      }, timeoutMs);

      const query = uid
        ? firebase.firestore().collection(FirebaseConstants.customers).doc(uid)
        : firebase.firestore().collection(FirebaseConstants.customers).where('email', '==', email);

      unsubscribe = (query as any).onSnapshot((result: any) => {
        if (uid) {
          if (result && result.exists) {
            const customerNumber = readCustomerNumber(result.data());
            if (customerNumber !== null) {
              settle(customerNumber);
            }
          }
          return;
        }
        const docs = (result && result.docs) || [];
        for (const doc of docs) {
          const customerNumber = readCustomerNumber(doc.data());
          if (customerNumber !== null) {
            settle(customerNumber);
            return;
          }
        }
      }, (error: any) => {
        console.error('[waitForCustomerNumber] Listener error:', error);
        settle(null);
      });
    });
  }

  retreiveUserPlan(userEmail: string): Promise<any> {
    const uid = this.authService.returnUserId();
    const normalizedEmail = typeof userEmail === 'string' ? userEmail.trim() : '';
    if (uid) {
      return firebase.firestore().collection(FirebaseConstants.customers)
        .doc(uid)
        .get()
        .then((docSnap) => {
          if (docSnap.exists && docSnap.data().plan) {
            console.log("retreiveUserPlan - Found plan via UID doc:", docSnap.data().plan);
            return docSnap.data().plan;
          }
          if (!normalizedEmail) {
            console.log("retreiveUserPlan - No email provided for fallback query");
            return null;
          }
          return this.retreiveUserPlanByEmail(normalizedEmail);
        }).catch(() => {
          if (!normalizedEmail) {
            return null;
          }
          return this.retreiveUserPlanByEmail(normalizedEmail);
        });
    }
    if (!normalizedEmail) {
      console.log("retreiveUserPlan - Missing email and uid, skipping query");
      return Promise.resolve(null);
    }
    return this.retreiveUserPlanByEmail(normalizedEmail);
  }

  // Duplicate customer documents per email exist in the wild. Firestore returns
  // them in no particular order, so pick the newest document that carries a plan
  // instead of whichever one happens to come back first.
  static readCreatedAtMs(data: any): number {
    const candidates = [data?.createdAt, data?.created, data?.createdISODateTime, data?.createdIsoTime, data?.createdIso, data?.createdISO];
    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }
      if (typeof candidate.toDate === 'function') {
        const asDate = candidate.toDate();
        if (asDate instanceof Date && !isNaN(asDate.getTime())) {
          return asDate.getTime();
        }
      }
      if (candidate instanceof Date && !isNaN(candidate.getTime())) {
        return candidate.getTime();
      }
      if (typeof candidate === 'number' && !isNaN(candidate)) {
        return candidate;
      }
      if (typeof candidate === 'string') {
        const parsed = new Date(candidate).getTime();
        if (!isNaN(parsed)) {
          return parsed;
        }
      }
    }
    return 0;
  }

  private retreiveUserPlanByEmail(userEmail: string): Promise<any> {
    if (!userEmail) {
      console.log("retreiveUserPlan - Missing email for query");
      return Promise.resolve(null);
    }
    return firebase.firestore().collection(FirebaseConstants.customers)
      .where('email', '==', userEmail)
      .get()
      .then((snap) => {
        console.log("retreiveUserPlan - Snapshot docs:", snap.docs.length);
        const withPlan = snap.docs
          .map(doc => doc.data())
          .filter(data => !!(data && data.plan))
          .sort((a, b) => FirebaseService.readCreatedAtMs(b) - FirebaseService.readCreatedAtMs(a));

        if (withPlan.length === 0) {
          console.log("retreiveUserPlan - No customer document with a plan for this email");
          return null;
        }
        if (withPlan.length > 1) {
          console.warn(`retreiveUserPlan - ${withPlan.length} customer documents carry a plan; using the most recent one`);
        }
        console.log("retreiveUserPlan - Returning plan:", withPlan[0].plan);
        return withPlan[0].plan;
      }).catch((err) => {
        console.error("retreiveUserPlan - Error retrieving plan:", err);
        return null;
      });
  }


  retreiveCustomerNumber() {
    const userEmail = this.authService.returnUser();
    return firebase.firestore().collection(FirebaseConstants.customers)
      .where('email', '==', userEmail)
      .get()
      .then((snap) => {
        snap.forEach((doc) => {
          return doc.data().kundennummer;
        });
      });
  }



  saveZvooveAPIKeys(data) {
    return this.getProfile().get().then((snap) => {
      const docs = snap.docs;
      const docLength = docs.length;
      if (docLength > 0) {
        const id = docs[0].id;
        return firebase.firestore().collection(FirebaseConstants.branding).doc(id).update({
          zvoove: data.zvoove,
          zvooveBewerberApiKey: data.zvooveBewerberApiKey || "",
          zvooveBewerberApiLink: data.zvooveBewerberApiLink || "",
          zvooveStellenApiKey: data.zvooveStellenApiKey || "",
          zvooveStellenApiLink: data.zvooveStellenApiLink || "",
          zvoooveNextLevelApiKey: data.zvoooveNextLevelApiKey || "",
          zvooveNextLevelApiLink: data.zvooveNextLevelApiLink || "",
          zvooveBewerberApiLinkById: data.zvooveBewerberApiLinkById || "",
          zvooveCreateBewerberApiLink: data.zvooveCreateBewerberApiLink || "",
          username: data.username || "",
          password: data.password || "",
          AIMatchingType: data.AIMatchingType || "",
          updated: new Date(),
          status: "aktiv"
        })
      } else {
        return false;
      }
    })
  }

  saveTalentionAPIKeys(data) {
    return this.getProfile().get().then((snap) => {
      const docs = snap.docs;
      const docLength = docs.length;
      if (docLength > 0) {
        const id = docs[0].id;
        return firebase.firestore().collection(FirebaseConstants.branding).doc(id).update({
          talention: data.talention,
          talentionClientId: data.talentionClientId || "",
          talentionClientSecret: data.talentionClientSecret || "",
          talentionAccountId: data.talentionAccountId || "",
          talentionAccountOption: data.talentionAccountOption || "",
          talentionAccountOptionBranches: data.talentionAccountOptionBranches || "",
          talentionMonaStatusEingang: data.talentionMonaStatusEingang || "",
          talentionMonaStatusImInterview: data.talentionMonaStatusImInterview || "",
          talentionMonaStatusInterviewAbgeschlossen: data.talentionMonaStatusInterviewAbgeschlossen || "",
          talentionMonaStatusAbgelehnt: data.talentionMonaStatusAbgelehnt || "",
          talentionMonaStatusProfilErstellt: data.talentionMonaStatusProfilErstellt || "",
          talentionMonaStatusProfilVersendet: data.talentionMonaStatusProfilVersendet || "",
          AIMatchingType: data.AIMatchingType || "",
          updated: new Date(),
          status: "aktiv"
        })
      } else {
        return false;
      }
    })
  }

  saveCompleetAPIKeys(data) {
    return this.getProfile().get().then((snap) => {
      const docs = snap.docs;
      const docLength = docs.length;
      if (docLength > 0) {
        const id = docs[0].id;
        return firebase.firestore().collection(FirebaseConstants.branding).doc(id).update({
          compleet: data.compleet,
          compleetAPIKeys: data.compleetAPIKeys || "",
          updated: new Date(),
          AIMatchingType: data.AIMatchingType || "",
          status: "aktiv"
        })
      } else {
        return false;
      }
    })
  }

  saveIndexAPIKeys(data) {
    return this.getProfile().get().then((snap) => {
      const docs = snap.docs;
      const docLength = docs.length;
      if (docLength > 0) {
        const id = docs[0].id;
        return firebase.firestore().collection(FirebaseConstants.branding).doc(id).update({
          index: data.index,
          indexLogin: data.indexLogin || "",
          indexPassword: data.indexPassword || "",
          updated: new Date(),
        })
      } else {
        return false;
      }
    })
  }

  saveZapierAPIKeys(data) {
    return this.getProfile().get().then((snap) => {
      const docs = snap.docs;
      const docLength = docs.length;
      if (docLength > 0) {
        const id = docs[0].id;
        return firebase.firestore().collection(FirebaseConstants.branding).doc(id).update({
          zapier: data.zapier,
          updated: new Date(),
          status: "aktiv"
        })
      } else {
        return false;
      }
    })
  }

  saveCustomIntegrationAPIKeys(data) {
    return this.getProfile().get().then((snap) => {
      const docs = snap.docs;
      const docLength = docs.length;
      if (docLength > 0) {
        const id = docs[0].id;
        return firebase.firestore().collection(FirebaseConstants.branding).doc(id).update({
          customIntegration: data.customIntegration,
          updated: new Date(),
          status: "aktiv"
        })
      } else {
        return false;
      }
    })
  }

  saveTalent360APIKeys(data) {
    return this.getProfile().get().then((snap) => {
      const docs = snap.docs;
      const docLength = docs.length;
      if (docLength > 0) {
        const id = docs[0].id;
        return firebase.firestore().collection(FirebaseConstants.branding).doc(id).update({
          talent360: data.talent360,
          talentClientId: data.talentClientId || "",
          talentClientSecret: data.talentClientSecret || "",
          talentAPIBaseUrl: data.talentAPIBaseUrl || "",
          talentAccessTokenUrl: data.talentAccessTokenUrl || "",
          talentPublicAudience: data.talentPublicAudience || "",
          updated: new Date(),
          status: "aktiv"
        })
      } else {
        return false;
      }
    })
  }

  getUpdates() {
    return this.updates;
  }

  addUpdate(update) {
    return this.updates.add(update);
  }

  // Add method to create customer-specific important messages
  async addCustomerImportantMessage(customerDB: any, update: any) {
    if (customerDB.app2) {
      return customerDB.app2.firestore
        .collection('importantMessages')
        .add(update);
    } else {
      throw new Error('Customer database not initialized');
    }
  }

  getUpdateById(id) {
    return this.updates.doc(id);
  }

  updateUpdate(id, update) {
    return this.updates.doc(id).update(update);
  }

  deleteUpdate(id) {
    return this.updates.doc(id).delete();
  }


}
